import { Center, Grid, Group, Paper, RingProgress, Stack, Text, ThemeIcon } from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import { IconActivity } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { formatDate } from './request-logger.utils';

import type { Session } from '@admin/hooks';
interface SessionDetailsCardProps {
  session: Session;
  duration: number;
}

export const SessionDetailsCard = ({ session, duration }: SessionDetailsCardProps) => {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  const interval = useInterval(() => {
    const remaining = calculateTimeRemaining();
    setTimeRemaining(remaining);

    if (remaining <= 0) {
      interval.stop();
    }
  }, 1000);

  const calculateTimeRemaining = () => {
    const startedAt = new Date(session.startedAt).getTime();
    const expiresAt = startedAt + duration;
    const now = Date.now();
    const remaining = expiresAt - now;

    return remaining > 0 ? remaining : 0;
  };

  useEffect(() => {
    setTimeRemaining(calculateTimeRemaining());
  }, [session, duration]);

  useEffect(() => {
    interval.start();
    return interval.stop;
  }, []);

  const formatTimeRemaining = (ms: number | null) => {
    if (ms === null) return 'N/A';
    if (ms <= 0) return 'Expired';

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    return `${minutes}m ${seconds}s`;
  };

  const getCountdownColor = (ms: number | null) => {
    if (ms === null || ms <= 0) return 'red';
    const minutes = ms / 60000;
    if (minutes < 2) return 'red';
    if (minutes < 5) return 'yellow';
    return 'green';
  };

  const calculateProgress = () => {
    if (!timeRemaining || !duration) return 0;
    if (timeRemaining <= 0) return 0;
    return (timeRemaining / duration) * 100;
  };

  return (
    <Paper shadow="xs" p="xl" radius="md" withBorder>
      <Group justify="space-between" align="flex-start">
        <Stack gap="md" style={{ flex: 1 }}>
          <Group gap="xs">
            <ThemeIcon color="cyan" variant="light" radius="xl" size="md">
              <IconActivity size={16} />
            </ThemeIcon>
            <Text size="lg" fw={600}>
              Active Session Details
            </Text>
          </Group>

          <Grid gutter="md">
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Session ID
                </Text>
                <Text size="sm" style={{ fontFamily: 'monospace' }}>
                  {session._id}
                </Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Started At
                </Text>
                <Text size="sm">{formatDate(session.startedAt)}</Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>

        <Center>
          <Stack gap="xs" align="center">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Time Remaining
            </Text>
            <RingProgress
              size={120}
              thickness={6}
              roundCaps
              sections={[{ value: calculateProgress(), color: getCountdownColor(timeRemaining) }]}
              label={
                <Center>
                  <Stack gap={0} align="center">
                    <Text size="md" fw={700} ta="center">
                      {formatTimeRemaining(timeRemaining)}
                    </Text>
                    <Text size="xs" c="dimmed" ta="center">
                      remaining
                    </Text>
                  </Stack>
                </Center>
              }
            />
          </Stack>
        </Center>
      </Group>
    </Paper>
  );
};
